import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, Mail, Phone, Building2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Page, FadeUp, SectionHead, MaskedLines } from "@/components/Reveal";
import ZipCTA from "@/components/denim/ZipCTA";

const FAQS = [
    {
        q: "How do I choose the right fit in premium denim jeans?",
        a: "Lead with comfort, then silhouette. Slim is clean and tailored; regular is relaxed and easy. Every product page carries a full size guide — and our desk is one message away.",
    },
    {
        q: "What's the difference between slim fit and regular fit jeans?",
        a: "Slim fit jeans are narrow through the legs for a clean, modern appearance. Regular fit jeans have a relaxed cut with more space for movement — ideal for all-day, everyday wear.",
    },
    {
        q: "Are stretch denim jeans better than pure cotton denim?",
        a: "Stretch denim includes elastane for flexibility and ease of movement, while pure cotton is more rigid and durable. We engineer premium stretch blends that stay comfortable without losing shape.",
    },
    {
        q: "How often should I wash my denim jeans?",
        a: "After 3–5 wears rather than after every use. Washing less preserves colour, fabric strength and overall durability — a habit every slow-fashion wardrobe benefits from.",
    },
    {
        q: "Do denim jeans shrink after washing?",
        a: "Pure cotton denim can shrink slightly after its first wash. URWAN denim is pre-washed and stretch-blended to hold its fit, wash after wash.",
    },
];

const emptyForm = { first: "", last: "", phone: "", email: "", message: "" };

export default function Contact() {
    const [form, setForm] = useState(emptyForm);
    const [openFaq, setOpenFaq] = useState(0);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = (e) => {
        e.preventDefault();
        if (!form.first.trim() || !form.phone.trim() || !form.email.trim()) {
            toast.error("Please fill in all required fields");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            toast.error("Please enter a valid email address");
            return;
        }
        toast.success("Received — expect a reply within one working day.");
        setForm(emptyForm);
    };

    const input =
        "w-full border border-white/15 bg-coal px-4 py-3.5 text-sm text-ink placeholder:text-mute/50 focus:border-gold focus:outline-none transition-colors";
    const label = "mb-2 block text-[10px] font-mono uppercase tracking-[0.25em] text-mute";

    return (
        <Page className="pb-28">
            <section className="mx-auto w-full max-w-[1400px] px-5 pb-14 pt-36 md:px-8 md:pt-44 lg:px-10">
                <p className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.3em] text-gold">
                    <span className="inline-block h-px w-10 bg-gold" /> Contact Us
                </p>
                <h1 className="mt-5 font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight sm:text-6xl">
                    <MaskedLines lines={["Get In Touch."]} />
                </h1>
                <p className="mt-5 max-w-xl text-sm leading-relaxed text-mute md:text-base">
                    Sizing, styling, bulk orders — the URWAN desk replies within one working day.
                </p>
            </section>

            <section className="mx-auto grid w-full max-w-[1400px] gap-10 px-5 md:px-8 lg:grid-cols-12 lg:px-10">
                <FadeUp className="lg:col-span-7">
                    <form
                        onSubmit={submit}
                        data-testid="contact-form"
                        className="border border-white/10 bg-coal/60 p-7 md:p-10"
                    >
                        <div className="grid gap-6 sm:grid-cols-2">
                            <div>
                                <label className={label} htmlFor="c-first">First Name *</label>
                                <input id="c-first" data-testid="contact-first-name" className={input} value={form.first} onChange={set("first")} placeholder="Your first name" />
                            </div>
                            <div>
                                <label className={label} htmlFor="c-last">Last Name</label>
                                <input id="c-last" data-testid="contact-last-name" className={input} value={form.last} onChange={set("last")} placeholder="Your last name" />
                            </div>
                            <div>
                                <label className={label} htmlFor="c-phone">Phone Number *</label>
                                <div className="flex items-center border border-white/15 bg-coal transition-colors focus-within:border-gold">
                                    <span className="border-r border-white/15 px-4 py-3.5 font-mono text-sm text-mute">+91</span>
                                    <input id="c-phone" data-testid="contact-phone" type="tel" className="w-full bg-transparent px-4 py-3.5 text-sm text-ink placeholder:text-mute/50 focus:outline-none" value={form.phone} onChange={set("phone")} placeholder="98765 43210" />
                                </div>
                            </div>
                            <div>
                                <label className={label} htmlFor="c-email">Email Address *</label>
                                <input id="c-email" data-testid="contact-email" type="email" className={input} value={form.email} onChange={set("email")} placeholder="you@example.com" />
                            </div>
                            <div className="sm:col-span-2">
                                <label className={label} htmlFor="c-msg">Message</label>
                                <textarea id="c-msg" data-testid="contact-message" rows={5} className={`${input} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us about your order, sizing or a bulk enquiry..." />
                            </div>
                        </div>
                        <ZipCTA type="submit" testid="contact-form-submit" className="mt-8">
                            Send Message
                        </ZipCTA>
                    </form>
                </FadeUp>

                <div className="space-y-6 lg:col-span-5">
                    {[
                        { icon: MapPin, label: "The Studio", body: "C011, SSVR Urban Flora, Immadihalli Main Road, Whitefield, Bangalore – 560066", href: null, id: "address" },
                        { icon: Mail, label: "Email Us", body: "info@urwan.com", href: "mailto:info@urwan.com", id: "email" },
                        { icon: Phone, label: "Call Us", body: "97380 02530", href: "tel:9738002530", id: "phone" },
                        { icon: Building2, label: "Corporate & Bulk Orders", body: "Write to support@urwan.in", href: "mailto:support@urwan.in", id: "bulk" },
                    ].map((c, i) => (
                        <FadeUp key={c.id} delay={i * 0.08}>
                            <div className="group flex items-start gap-5 border border-white/10 bg-coal/60 p-6 transition-colors duration-300 hover:border-gold/50">
                                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-gold/40 text-gold transition-colors group-hover:bg-gold group-hover:text-black">
                                    <c.icon size={18} />
                                </span>
                                <div>
                                    <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold">{c.label}</p>
                                    {c.href ? (
                                        <a href={c.href} data-testid={`contact-info-${c.id}`} className="mt-2 block text-sm leading-relaxed text-ink transition-colors hover:text-gold">
                                            {c.body}
                                        </a>
                                    ) : (
                                        <p className="mt-2 text-sm leading-relaxed text-ink">{c.body}</p>
                                    )}
                                </div>
                            </div>
                        </FadeUp>
                    ))}
                </div>
            </section>

            <section className="mx-auto w-full max-w-[900px] px-5 pt-24 md:px-8">
                <SectionHead eyebrow="Frequently Asked Questions" title="Before You Ask" align="center" />
                <div className="mt-12 border-t border-white/10" data-testid="faq-accordion">
                    {FAQS.map((f, i) => (
                        <div key={i} className="border-b border-white/10">
                            <button
                                data-testid={`faq-toggle-${i}`}
                                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                                className="flex w-full items-center justify-between gap-6 py-5 text-left"
                            >
                                <span className="font-display text-base font-semibold text-ink md:text-lg">
                                    {f.q}
                                </span>
                                <Plus
                                    size={18}
                                    className={`shrink-0 transition-transform duration-300 ${
                                        openFaq === i ? "rotate-45 text-gold" : "text-mute"
                                    }`}
                                />
                            </button>
                            <AnimatePresence initial={false}>
                                {openFaq === i && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                        className="overflow-hidden"
                                    >
                                        <p className="pb-6 text-sm leading-relaxed text-mute">{f.a}</p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                </div>
            </section>
        </Page>
    );
}
